import React, { useCallback, useEffect, useRef, useState } from "react";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import {
    advance,
    ballOn,
    Defense,
    depthAt,
    DRIVES_PER_GAME,
    FIELD_LENGTH,
    FLIGHT_MS,
    FootballMiss,
    FootballState,
    isGoalToGo,
    nextPlay,
    Play,
    prettyFront,
    RECEIVER_COUNT,
    rushProgress,
    startGame,
    throwTo,
    yardsToGo,
} from "./footballGame.ts";
import { cardFaces } from "../cardFaces.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

export interface FootballResult {
    defense: Defense;
    points: number;
    touchdowns: number;
    completions: number;
    attempts: number;
    wrongThrows: number;
    sacks: number;
    misses: FootballMiss[];
    correctFronts: string[];
}

interface FootballGameRunProps {
    queue: QuizQueue;
    defense: Defense;
    onFinish(result: FootballResult): void;
}

/*
 * The play view is drawn in percentages of a square. The quarterback stands in
 * the backfield, the bottom BACKFIELD percent; the line of scrimmage is above
 * it, and the next WINDOW_YARDS yards of the field fill the rest. The deepest
 * route is 30 yards, so every receiver always fits.
 */
const WINDOW_YARDS = 32;
const BACKFIELD = 14;
/** Each receiver's lane, as a percentage across the field. */
const LANE_LEFT = [12.5, 37.5, 62.5, 87.5];

/** Distance downfield of the line of scrimmage, as a percentage up the field. */
function fieldBottom(yards: number): number {
    return BACKFIELD + (yards / WINDOW_YARDS) * (100 - BACKFIELD);
}

/** The whole-field strip: end zones top and bottom, 100 yards between. */
const STRIP_END = 8;
function stripBottom(yard: number): number {
    return STRIP_END + (yard / FIELD_LENGTH) * (100 - 2 * STRIP_END);
}

/*
 * Written with literal `t("...")` calls rather than a lookup table so
 * StringKeys.test.js can see every key used.
 */
function downText(t: (key: StringKey) => string, down: number): string {
    switch (down) {
        case 1:
            return t("flashCards.footballDownFirst");
        case 2:
            return t("flashCards.footballDownSecond");
        case 3:
            return t("flashCards.footballDownThird");
        default:
            return t("flashCards.footballDownFourth");
    }
}

function spotText(t: (key: StringKey) => string, ball: number): string {
    const spot = ballOn(ball);
    return spot.side === "own"
        ? format(t("flashCards.footballOwnTemplate"), { n: spot.yard })
        : format(t("flashCards.footballOppTemplate"), { n: spot.yard });
}

/** What the last play was, in words, for the verdict line and the live region. */
function playText(t: (key: StringKey) => string, play: Play): string {
    const parts: string[] = [];
    const front = prettyFront(play.front);
    if (play.kind === "touchdown") {
        parts.push(t("flashCards.footballTouchdown"));
    } else if (play.kind === "catch") {
        parts.push(
            play.yards === 1
                ? t("flashCards.footballCaughtOne")
                : format(t("flashCards.footballCaughtTemplate"), { n: play.yards })
        );
        if (play.firstDown) {
            parts.push(t("flashCards.footballFirstDown"));
        }
    } else if (play.kind === "incomplete") {
        parts.push(format(t("flashCards.footballIncompleteTemplate"), { front, answer: play.answer }));
    } else {
        // A sack on the 1 loses nothing (ADR 0103 §3), so it says only "Sacked".
        const lost = -play.yards;
        const sacked =
            lost === 0
                ? t("flashCards.footballSackedNoLossTemplate")
                : lost === 1
                  ? t("flashCards.footballSackedOneTemplate")
                  : t("flashCards.footballSackedTemplate");
        parts.push(format(sacked, { n: lost, front, answer: play.answer }));
    }
    if (play.turnover) {
        parts.push(t("flashCards.footballTurnover"));
    }
    return parts.join(" ");
}

const VERDICT_CLASS: Record<Play["kind"], string> = {
    catch: "correct",
    touchdown: "correct",
    incomplete: "wrong",
    sack: "skipped",
};

/** Yard lines, the line to gain, the line of scrimmage and the end zone. Decoration. */
function Turf({ snapBall, snapLineToGain }: { snapBall: number; snapLineToGain: number }) {
    const t = useTranslation();
    const lines: number[] = [];
    for (let yard = Math.floor(snapBall / 5) * 5 + 5; yard < FIELD_LENGTH && yard - snapBall <= WINDOW_YARDS; yard += 5) {
        lines.push(yard);
    }
    const goalDistance = FIELD_LENGTH - snapBall;
    const endZoneShown = goalDistance <= WINDOW_YARDS;
    const gainDistance = snapLineToGain - snapBall;

    return (
        <div className="footballTurf" aria-hidden="true">
            {lines.map((yard) => (
                <div key={yard} className="footballYardLine" style={{ bottom: `${fieldBottom(yard - snapBall)}%` }}>
                    <span className="footballYardNumber">{yard <= FIELD_LENGTH / 2 ? yard : FIELD_LENGTH - yard}</span>
                </div>
            ))}
            {endZoneShown && (
                <div className="footballEndZone" style={{ bottom: `${fieldBottom(goalDistance)}%` }}>
                    <span>{t("flashCards.footballEndZone")}</span>
                </div>
            )}
            {gainDistance < goalDistance && gainDistance <= WINDOW_YARDS && (
                <div className="footballLineToGain" style={{ bottom: `${fieldBottom(gainDistance)}%` }} />
            )}
            <div className="footballScrimmage" style={{ bottom: `${BACKFIELD}%` }} />
        </div>
    );
}

/** The whole field, end to end, with the ball and the line to gain. Decoration: the scoreboard has the words. */
function Strip({ ball, lineToGain }: { ball: number; lineToGain: number }) {
    return (
        <div className="footballStrip" aria-hidden="true">
            <div className="footballStripEndZone footballStripEndZone--far" style={{ height: `${STRIP_END}%` }} />
            <div className="footballStripEndZone footballStripEndZone--near" style={{ height: `${STRIP_END}%` }} />
            {lineToGain < FIELD_LENGTH && (
                <div className="footballStripGain" style={{ bottom: `${stripBottom(lineToGain)}%` }} />
            )}
            <div className="footballStripBall" style={{ bottom: `${stripBottom(ball)}%` }} />
        </div>
    );
}

/**
 * One game: four drives, a play per fact (ADR 0103).
 *
 * The engine is pure (`footballGame.ts`); this feeds it animation-frame deltas
 * and throws. State lives in a ref with a snapshot published per frame, as in
 * SoccerGameRun, because `advance` and `nextPlay` draw from the queue (0053 §8).
 *
 * A throw is a tap on a receiver or the key 1 to 4. After a play there is a
 * short result, with the right answer shown, and then the next fact; "Next
 * play" moves it on sooner. Receivers are buttons that stay in the page (and
 * keep focus) through the countdown, a pause and the result, only answering to
 * a press during a play, so focus never falls out of the field.
 *
 * Play time pauses on the Pause button and by itself when the tab is hidden.
 * The fact and the numbers are hidden while paused, so a pause is not a way to
 * read them at leisure. Resuming is always a press.
 */
export function FootballGameRun({ queue, defense, onFinish }: FootballGameRunProps) {
    const t = useTranslation();
    const stateRef = useRef<FootballState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startGame(defense, queue, getRandomInt);
    }
    const [view, setView] = useState<FootballState>(stateRef.current);
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(false);
    pausedRef.current = paused;
    const receiverRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const reported = useRef(false);

    const over = view.phase === "over";

    useEffect(() => {
        if (paused || over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            // advance() clamps a negative or oversized delta itself.
            const next = advance(stateRef.current!, now - last, queue, getRandomInt);
            last = now;
            // A frame that changed nothing (the countdown is over, say) re-renders nothing.
            if (next !== stateRef.current) {
                stateRef.current = next;
                setView(next);
            }
            if (next.phase !== "over") {
                frame = requestAnimationFrame(tick);
            }
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [paused, over, queue]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                setPaused(true);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        receiverRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            const s = stateRef.current!;
            onFinish({
                defense: s.defense,
                points: s.points,
                touchdowns: s.touchdowns,
                completions: s.completions,
                attempts: s.attempts,
                wrongThrows: s.wrongThrows,
                sacks: s.sacks,
                misses: s.misses,
                correctFronts: s.correctFronts,
            });
        }
    }, [over, onFinish]);

    const throwAt = useCallback((lane: number) => {
        if (pausedRef.current) {
            return;
        }
        const next = throwTo(stateRef.current!, lane);
        if (next !== stateRef.current) {
            stateRef.current = next;
            setView(next);
        }
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) {
                return;
            }
            const target = e.target as HTMLElement | null;
            if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
                return;
            }
            const lane = "1234".indexOf(e.key);
            if (lane >= 0 && lane < RECEIVER_COUNT) {
                e.preventDefault();
                throwAt(lane);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [throwAt]);

    function skipResult() {
        if (pausedRef.current || stateRef.current!.phase !== "result") {
            return;
        }
        const next = nextPlay(stateRef.current!, queue, getRandomInt);
        stateRef.current = next;
        setView(next);
    }

    const playing = view.phase === "playing" && !paused;
    const masked = paused || view.phase === "countdown";
    const showingResult = view.phase === "result" || view.phase === "over";
    const play = view.lastPlay;
    const faces = cardFaces(view.problem);
    const countdownSeconds = Math.max(1, Math.ceil(view.countdownLeftMs / 1000));
    const distance = isGoalToGo(view) ? t("flashCards.footballGoal") : yardsToGo(view);
    const verdictClass = play ? VERDICT_CLASS[play.kind] : null;

    // The ball flies from the quarterback to the receiver it was thrown to,
    // wherever that receiver had got to.
    const ball =
        play && play.thrownTo !== null && showingResult
            ? {
                  left: LANE_LEFT[play.thrownTo],
                  bottom: fieldBottom(depthAt(play.thrownTo, view.playMs)),
              }
            : null;

    return (
        <div className="quizRun footballRun">
            <div className="quizStatus footballStatus">
                <span className="quizScore">{format(t("flashCards.footballPointsTemplate"), { n: view.points })}</span>
                <span>
                    {format(t("flashCards.footballDriveTemplate"), { n: view.drive, total: DRIVES_PER_GAME })}
                </span>
                <span className="footballDown">
                    {format(t("flashCards.footballDownAndDistanceTemplate"), {
                        down: downText(t, view.down),
                        distance,
                    })}
                </span>
                <span>{format(t("flashCards.footballBallOnTemplate"), { spot: spotText(t, view.ball) })}</span>
            </div>

            <p className="footballFact" aria-live="polite">
                {masked ? " " : `${prettyFront(faces.front)} = ?`}
            </p>

            <div className="footballPlay">
                <div className="footballField" role="group" aria-label={t("flashCards.footballFieldAria")}>
                    <Turf snapBall={view.snapBall} snapLineToGain={view.snapLineToGain} />

                    <div
                        className={`footballQb${play?.kind === "sack" ? " footballQb--sacked" : ""}`}
                        style={{ bottom: `${BACKFIELD / 2}%` }}
                        aria-hidden="true"
                    >
                        {t("flashCards.footballQuarterback")}
                    </div>

                    {Array.from({ length: RECEIVER_COUNT }, (_, lane) => {
                        const value = view.numbers[lane];
                        const state =
                            showingResult && play
                                ? lane === play.rightLane
                                    ? " footballReceiver--right"
                                    : lane === play.thrownTo
                                    ? " footballReceiver--wrong"
                                    : ""
                                : "";
                        return (
                            <button
                                key={lane}
                                ref={(el) => {
                                    receiverRefs.current[lane] = el;
                                }}
                                type="button"
                                className={`footballReceiver${state}`}
                                style={{
                                    left: `${LANE_LEFT[lane]}%`,
                                    bottom: `${fieldBottom(depthAt(lane, view.playMs))}%`,
                                }}
                                // Not `disabled`: a disabled button drops keyboard focus.
                                aria-disabled={!playing}
                                aria-label={
                                    masked
                                        ? format(t("flashCards.footballReceiverHiddenAriaTemplate"), { n: lane + 1 })
                                        : format(t("flashCards.footballReceiverAriaTemplate"), {
                                              n: lane + 1,
                                              value,
                                          })
                                }
                                onClick={() => throwAt(lane)}
                            >
                                <span className="footballJersey" aria-hidden="true">
                                    {masked ? "?" : value}
                                </span>
                                <span className="footballLane" aria-hidden="true">
                                    {lane + 1}
                                </span>
                            </button>
                        );
                    })}

                    {ball && play && (
                        <span
                            key={`ball-${play.number}`}
                            className="footballBall"
                            aria-hidden="true"
                            style={
                                {
                                    "--bx": `${ball.left}%`,
                                    "--by": `${ball.bottom}%`,
                                    "--qb": `${BACKFIELD / 2}%`,
                                    "--flight": `${FLIGHT_MS}ms`,
                                } as React.CSSProperties
                            }
                        />
                    )}

                    {paused ? (
                        <div className="footballOverlay">
                            <p className="footballOverlayTitle">{t("flashCards.gamePaused")}</p>
                            <p className="footballOverlayHint">{t("flashCards.gamePausedHint")}</p>
                        </div>
                    ) : view.phase === "countdown" ? (
                        <div className="footballOverlay" aria-live="assertive">
                            <p className="quizCountdownNumber">{countdownSeconds}</p>
                            <p className="footballOverlayHint">{t("flashCards.getReady")}</p>
                        </div>
                    ) : null}
                </div>

                <Strip ball={view.ball} lineToGain={view.lineToGain} />
            </div>

            <div className="footballRush" aria-hidden="true">
                <span className="footballRushLabel">{t("flashCards.footballRush")}</span>
                <span className="footballRushTrack">
                    <span
                        className="footballRushFill"
                        style={{ width: `${Math.round(rushProgress(view) * 100)}%` }}
                    />
                </span>
            </div>

            {/* Always rendered, always the same height, as on the quiz. */}
            <p className={`quizVerdict${verdictClass ? ` quizVerdict--${verdictClass}` : ""}`} aria-live="polite">
                {play && <span key={play.number}>{playText(t, play)}</span>}
            </p>

            <div className="footballButtons">
                <button
                    type="button"
                    className="quizFinish footballPause"
                    onClick={() => setPaused((p) => !p)}
                    disabled={over}
                >
                    {paused ? t("flashCards.gameResume") : t("flashCards.gamePause")}
                </button>
                <button
                    type="button"
                    className="quizFinish footballNext"
                    onClick={skipResult}
                    aria-disabled={view.phase !== "result" || paused}
                >
                    {t("flashCards.footballNextPlay")}
                </button>
            </div>

            <p className="quizHint">{t("flashCards.footballHint")}</p>
        </div>
    );
}
